//
// Copyright © 2026 Zolvat Ltd
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

export type DateFormatPreference = 'dmy' | 'ymd' | 'mdy'

export interface DateFormatOption {
  id: DateFormatPreference
  label: string
  example: string
}

export const defaultDateFormatPreference: DateFormatPreference = 'dmy'

export const dateFormatOptions: DateFormatOption[] = [
  { id: 'dmy', label: 'Day/month/year', example: '15 May 2026' },
  { id: 'ymd', label: 'Year/month/day', example: '2026 May 15' },
  { id: 'mdy', label: 'Month/day/year', example: 'May 15, 2026' }
]

interface WritableStore<T> {
  subscribe: (run: (value: T) => void) => () => void
  set: (value: T) => void
}

const dateFormatStorageKey = 'dateFormatPreference'
const shortMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const getLocalStorage = (): Storage | undefined => {
  if (typeof globalThis === 'undefined') {
    return undefined
  }

  try {
    return globalThis.localStorage
  } catch {
    return undefined
  }
}

const getLocalStorageItem = (key: string): string | null => {
  const storage = getLocalStorage()
  if (storage === undefined) {
    return null
  }

  try {
    return storage.getItem(key)
  } catch {
    return null
  }
}

const setLocalStorageItem = (key: string, value: string): void => {
  const storage = getLocalStorage()
  if (storage === undefined) {
    return
  }

  try {
    storage.setItem(key, value)
  } catch {
    // Keep the live UI preference update working when browser storage is unavailable.
  }
}

const isDateFormatPreference = (value: unknown): value is DateFormatPreference =>
  value === 'dmy' || value === 'ymd' || value === 'mdy'

const createStore = <T>(initialValue: T): WritableStore<T> => {
  let value = initialValue
  const subscribers = new Set<(value: T) => void>()

  return {
    subscribe: (run: (value: T) => void) => {
      run(value)
      subscribers.add(run)
      return () => {
        subscribers.delete(run)
      }
    },
    set: (nextValue: T) => {
      value = nextValue
      subscribers.forEach((run) => {
        run(value)
      })
    }
  }
}

export const getCurrentDateFormatPreference = (): DateFormatPreference => {
  const stored = getLocalStorageItem(dateFormatStorageKey)
  return isDateFormatPreference(stored) ? stored : defaultDateFormatPreference
}

let currentDateFormatPreference = getCurrentDateFormatPreference()

export const dateFormatStore = createStore<DateFormatPreference>(currentDateFormatPreference)

export const setDateFormatPreference = (format: DateFormatPreference): void => {
  if (!isDateFormatPreference(format)) {
    return
  }

  setLocalStorageItem(dateFormatStorageKey, format)
  currentDateFormatPreference = format
  dateFormatStore.set(format)
}

export const getDateFormatOption = (format: DateFormatPreference = currentDateFormatPreference): DateFormatOption =>
  dateFormatOptions.find((option) => option.id === format) ?? dateFormatOptions[0]

const normalizeDate = (value: number | Date): Date => (value instanceof Date ? value : new Date(value))

const resolveDateFormat = (format?: DateFormatPreference): DateFormatPreference =>
  format ?? currentDateFormatPreference ?? defaultDateFormatPreference

const isSameLocalDay = (value: Date, now: Date): boolean =>
  value.getFullYear() === now.getFullYear() &&
  value.getMonth() === now.getMonth() &&
  value.getDate() === now.getDate()

export const formatFullDate = (value: number | Date, format?: DateFormatPreference): string => {
  const date = normalizeDate(value)
  const preference = resolveDateFormat(format)
  const day = date.getDate()
  const month = shortMonths[date.getMonth()]
  const year = date.getFullYear()

  switch (preference) {
    case 'ymd':
      return `${year} ${month} ${day}`
    case 'mdy':
      return `${month} ${day}, ${year}`
    case 'dmy':
    default:
      return `${day} ${month} ${year}`
  }
}

export const formatDayMonthDate = (
  value: number | Date,
  format?: DateFormatPreference,
  includeYear: boolean = false
): string => {
  const date = normalizeDate(value)
  const preference = resolveDateFormat(format)
  const day = date.getDate()
  const month = shortMonths[date.getMonth()]

  if (includeYear) {
    return formatFullDate(date, preference)
  }

  switch (preference) {
    case 'dmy':
      return `${day} ${month}`
    case 'ymd':
    case 'mdy':
    default:
      return `${month} ${day}`
  }
}

export const formatShortDate = (
  value: number | Date,
  format?: DateFormatPreference,
  includeYear: boolean = false
): string => {
  const date = normalizeDate(value)
  const preference = resolveDateFormat(format)
  const day = date.getDate()
  const month = date.getMonth() + 1
  const year = date.getFullYear()

  switch (preference) {
    case 'ymd':
      return includeYear ? `${year}/${month}/${day}` : `${year}/${month}/${day}`
    case 'mdy':
      return includeYear ? `${month}/${day}/${year}` : `${month}/${day}`
    case 'dmy':
    default:
      return includeYear ? `${day}/${month}/${year}` : `${day}/${month}`
  }
}

export const formatClockTime = (value: number | Date): string => {
  const options: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' }
  return normalizeDate(value).toLocaleTimeString('default', options)
}

export const formatShortTime = (value: number | Date): string => formatClockTime(value).replace(/\s?[ap]\.?m\.?$/i, '')

export const formatFullDateTime = (value: number | Date, format?: DateFormatPreference): string =>
  `${formatFullDate(value, format)}, ${formatClockTime(value)}`

export const formatWeekdayDate = (
  value: number | Date,
  format?: DateFormatPreference,
  includeYear: boolean = false
): string => {
  const date = normalizeDate(value)
  return `${weekdays[date.getDay()]}, ${formatDayMonthDate(date, format, includeYear)}`
}

export const formatDisplayDateTime = (
  value: number | Date,
  format?: DateFormatPreference,
  now: number | Date = Date.now()
): string => {
  const date = normalizeDate(value)
  if (isSameLocalDay(date, normalizeDate(now))) {
    return formatClockTime(date)
  }

  return `${formatShortDate(date, format)}, ${formatClockTime(date)}`
}
